import {BrowserModule} from '@angular/platform-browser';
import {APP_INITIALIZER, InjectionToken, Injector, LOCALE_ID, NgModule} from '@angular/core';
import {OAuthModule, OAuthModuleConfig, OAuthResourceServerErrorHandler, OAuthStorage} from 'angular-oauth2-oidc';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {LayoutComponent} from './layout/layout.component';
import {LayoutSimpleComponent} from './layout/layout-simple.component';
import {P404Component} from './404.component';
import {SharedModule} from './shared/shared.module';
import {NotificationsService, SimpleNotificationsModule} from 'angular2-notifications';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {CommonModule, LOCATION_INITIALIZED, registerLocaleData} from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import localeUk from '@angular/common/locales/uk';
import localeAz from '@angular/common/locales/az';
import localeRuExtra from '@angular/common/locales/extra/ru';
import localeUkExtra from '@angular/common/locales/extra/uk';
import localeAzExtra from '@angular/common/locales/extra/az';
import {RouterModule} from '@angular/router';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {AuthorizationService} from './authorization/authorization.service';
import {UserListService} from './user/user-list/user-list.service';
import {GridModule, RestSimpleResponseParser, RestYiiFilterService, RestYiiPagination, RestYiiSort} from 'gelios-ng-admin-components';
import {environment} from '../environments/environment';
import {UnitService} from './unit/unit.service';
import {LeafletModule} from '@asymmetrik/ngx-leaflet';
import {ImmutableListRepositoryService} from '../lib/gelios-field/immutable-list-repository.service';
import {UnitCardModule} from './unit-card/unit-card.module';
import {REST_BASE_URL, RestService as SimpleRestService} from '../lib/yii2-rest/rest.service';
import {LanguageInterceptor} from './language/LanguageInterceptor';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {ErrorForbiddenInterceptor} from './language/error-forbidden.interceptor';
import {UnitLogPopoverComponent} from './user-menu/unit-log-popover/unit-log-popover.component';
import {ReactiveFormsModule} from '@angular/forms';
import {AcceptanceCertificateService} from './project/acceptance-certificate/acceptance-certificate.service';
import {AppInitService} from './app-init.service';
import {ENVIRONMENT_AUTH_CONFIG} from './auth.config';
import {filter, map} from 'rxjs/operators';
import {OAuthCustomResourceServerErrorHandler} from './authorization/OAuthCustomResourceServerErrorHandler';
import {InjectableRxStompConfig, RxStompService, rxStompServiceFactory} from '@stomp/ng2-stompjs';
import {rxStompConfig} from './web-stomp/rx-stomp-config';
import {WebStompInfoComponent} from './layout/web-stomp-info/web-stomp-info.component';
import {OfflineUnitsComponent} from './layout/offline-units/offline-units.component';
import {IConfig, NgxMaskModule} from 'ngx-mask';
import {DeviceDetectorModule, DeviceDetectorService} from 'ngx-device-detector';
import {EndUserConfirmationInfoComponent} from './layout/end-user-confirmation-info/end-user-confirmation-info.component';
import {QRCodeModule} from 'angularx-qrcode';
import {UserBalanceComponent} from './layout/user-balance/user-balance.component';
import {MainLightboxComponent} from './layout/mainlightbox/main-lightbox.component';
import {MatSliderModule} from '@angular/material/slider';
import {GeliosFieldModule} from '../lib/gelios-field/gelios-field.module';
import {TranslationLoader} from './i18n/translation-loader';
import {OrderPhotosLightboxComponent} from './layout/order-photos-lightbox/order-photos-lightbox.component';
import {TranslationSelectorService} from './shared/translation-selector.service';
import {UserMenuInfoComponent} from './user-menu/user-menu-info/user-menu-info.component';
import {TimeInfoComponent} from './user-menu/time-info/time-info.component';
import {LanguageMenuComponent} from './user-menu/language-menu/language-menu.component';
import {FullscreenMenuComponent} from './user-menu/fullscreen-menu/fullscreen-menu.component';
import {UserSimBalanceMenuComponent} from './user-menu/user-sim-balance-menu/user-sim-balance-menu.component';
import {TopUpBalanceMenuComponent} from './user-menu/top-up-balance-menu/top-up-balance-menu.component';
import {WarningErrorMenuComponent} from './user-menu/warning-error-menu/warning-error-menu.component';
import {MobileTopMenuComponent} from './layout/mobile-top-menu/mobile-top-menu.component';
import {DesktopTopMenuComponent} from './layout/desktop-top-menu/desktop-top-menu.component';
import {
  ModalWindowTopUpBalanceComponent
} from './shared/modal-window/modal-window/modal-window-top-up-balance/modal-window-top-up-balance.component';
import {ModalWindowComponent} from './shared/modal-window/modal-window/modal-window.component';
import {TopUpBalanceUsingBankCardComponent} from './user/user-list/top-up-balance-using-bank-card/top-up-balance-using-bank-card.component';
import {MainNavigationComponent} from './layout/main-navigation/main-navigation.component';
import {LogoComponent} from './layout/logo/logo.component';
import {ModeratorPasswordPopoverComponent} from './layout/moderator-password-popover/moderator-password-popover.component';
import {ModeratorTokenInterceptor} from './shared/interceptors/moderator-token.interceptor';
import { ProjectBusinessLogicFilterComponent } from './user-menu/project-business-logic-filter/project-business-logic-filter.component';
import {OAuthInterceptor} from './authorization/oauth.interceptor';

registerLocaleData(localeRu, 'ru', localeRuExtra);
registerLocaleData(localeUk, 'uk', localeUkExtra);
registerLocaleData(localeAz, 'az', localeAzExtra);

export function createUserService(http: HttpClient, version, apiBasepoint) {
  return new UserListService(
    http,
    new RestYiiFilterService(),
    new RestYiiSort(),
    new RestYiiPagination(),
    new RestSimpleResponseParser(),
    version,
    apiBasepoint
  );
}
export function createUnitService(http: HttpClient, version, apiBasepoint) {
  return new UnitService(
    http,
    new RestYiiFilterService(),
    new RestYiiSort(),
    new RestYiiPagination(),
    new RestSimpleResponseParser(),
    version,
    apiBasepoint
  );
}
export function createAcceptanceCertificateService(http: HttpClient, version, apiBasepoint) {
  return new AcceptanceCertificateService(
    http,
    new RestYiiFilterService(),
    new RestYiiSort(),
    new RestYiiPagination(),
    new RestSimpleResponseParser(),
    version,
    apiBasepoint
  );
}
export function appInitializerFactory(translate: TranslateService, translateSelector: TranslationSelectorService, injector: Injector) {
  return () => new Promise<any>((resolve: any) => {
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    locationInitialized.then(() => {
      const lang = translateSelector.getSelectedLanguage();

      // const langToSet = 'en-GB'
      translate.setDefaultLang('en');
      translate.use(lang).subscribe(() => {
        // console.log(`Successfully initialized '${lang}' language.'`);
      }, err => {
        console.error(`Problem with '${lang}' language initialization.'`);
      }, () => {
        resolve(null);
      });
    });
  });
}

export function createAppInitService(appInit: AppInitService) {
  return () => appInit.loadConfiguration();
}

export function configEnvironmentVersionFactory() {
  return environment.version;
}

export function configApiBasepointFactory() {
  return environment.apiBasepoint;
}

export function createAuthConfig(
  service: AppInitService,
) {
  return {
    loginUrl: environment.authLoginUrl, // 'https://dev.api.busy-fly.com/login',
    logoutUrl: environment.authLogoutUrl, // 'https://dev.api.busy-fly.com/logout',
    redirectUri: window.location.href,
    postLogoutRedirectUri: window.location.origin,
    clientId: 'ItemSharingWebAdmin',
    requireHttps: false,
    oidc: false,
    clearHashAfterLogin: true,
    silentRefreshRedirectUri: window.location.origin + '/assets/silent-refresh.html',
  };
}

export const ENVIRONMENT_VERSION = new InjectionToken<string>('api version');
export const ENVIRONMENT_API_BASEPOINT  = new InjectionToken<string>('api basepoint');
const maskConfig: Partial<IConfig> = {
  validation: false,
};


@NgModule({
  declarations: [
    AppComponent,
    LayoutComponent,
    LayoutSimpleComponent,
    P404Component,
    UnitLogPopoverComponent,
    WebStompInfoComponent,
    OfflineUnitsComponent,
    EndUserConfirmationInfoComponent,
    UserBalanceComponent,
    MainLightboxComponent,
    OrderPhotosLightboxComponent,
    UserMenuInfoComponent,
    TimeInfoComponent,
    LanguageMenuComponent,
    FullscreenMenuComponent,
    UserSimBalanceMenuComponent,
    TopUpBalanceMenuComponent,
    WarningErrorMenuComponent,
    MobileTopMenuComponent,
    DesktopTopMenuComponent,
    ModalWindowComponent,
    ModalWindowTopUpBalanceComponent,
    TopUpBalanceUsingBankCardComponent,
    MainNavigationComponent,
    LogoComponent,
    ModeratorPasswordPopoverComponent,
    ProjectBusinessLogicFilterComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    DeviceDetectorModule,
    QRCodeModule,
    TranslateModule.forRoot({
      useDefaultLang: true,
      loader: {
        provide: TranslateLoader,
        useClass: TranslationLoader
      },
      isolate: false
    }),
    SimpleNotificationsModule,
    HttpClientModule,
    OAuthModule.forRoot(
      // {
      //   resourceServer: {
      //     allowedUrls: environment.allowedUrls,
      //     sendAccessToken: true
      //   }
      // }
    ),
    CommonModule,
    SimpleNotificationsModule.forRoot(),
    RouterModule,
    LeafletModule.forRoot(),
    UnitCardModule,
    ReactiveFormsModule,
    NgxMaskModule.forRoot(maskConfig),
    MatSliderModule,
    GridModule,
    GeliosFieldModule
  ],
    exports: [
        LeafletModule,
        TranslateModule,
    ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: createAppInitService,
      deps: [AppInitService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, TranslationSelectorService, Injector],
      multi: true
    },
    {
      provide: UserListService,
      useFactory: createUserService,
      deps: [HttpClient, ENVIRONMENT_VERSION, ENVIRONMENT_API_BASEPOINT]
    },
    {
      provide: UnitService,
      useFactory: createUnitService,
      deps: [HttpClient]
    },
    {
      provide: AcceptanceCertificateService,
      useFactory: createAcceptanceCertificateService,
      deps: [HttpClient]
    },
    {
      provide: ImmutableListRepositoryService,
      useClass: ImmutableListRepositoryService
    },
    {
      provide: 'ENVIRONMENT_VERSION',
      useFactory: configEnvironmentVersionFactory,
      multi: false
    },
    {
      provide: 'ENVIRONMENT_API_BASEPOINT',
      useFactory: configApiBasepointFactory,
      multi: false
    },
    {
      provide: ENVIRONMENT_VERSION,
      useFactory: configEnvironmentVersionFactory,
      multi: false
    },
    {
      provide: ENVIRONMENT_API_BASEPOINT,
      useFactory: configApiBasepointFactory,
      multi: false
    },
    {
      provide: REST_BASE_URL,
      useFactory: configApiBasepointFactory,
      multi: false
    },
    {
      provide: LOCALE_ID,
      useFactory: (translationSelectorService: TranslationSelectorService) => {
        const language = translationSelectorService.getSelectedLanguage();
        switch (language) {
          case 'ru':
            return 'ru-RU';
          case 'uk':
            return 'uk-UA';
          case 'az':
            return 'az-AZ';
          default:
            return 'en-US';
        }
      },
      deps: [TranslationSelectorService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OAuthInterceptor,
      deps: [OAuthStorage, OAuthResourceServerErrorHandler],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      deps: [TranslationSelectorService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ModeratorTokenInterceptor,
      deps: [AuthorizationService],
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorForbiddenInterceptor,
      deps: [NotificationsService, AuthorizationService],
      multi: true
    },
    {
      provide: ENVIRONMENT_AUTH_CONFIG,
      useFactory: createAuthConfig,
      deps: [AppInitService]
    },
    {
      provide: OAuthResourceServerErrorHandler,
      useClass: OAuthCustomResourceServerErrorHandler
    },
    {
      provide: InjectableRxStompConfig,
      useValue: rxStompConfig
    },
    {
      provide: RxStompService,
      useFactory: rxStompServiceFactory,
      deps: [InjectableRxStompConfig]
    },
    {
      provide: DeviceDetectorService,
      useClass: DeviceDetectorService,
    },
    {
      provide: SimpleRestService,
      useClass: SimpleRestService
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

